import { api, LONG_REQUEST_TIMEOUT_MS } from './client';
import { filenameFromContentDisposition } from '../lib/download';
import type {
  ApproveBatchResult,
  Business,
  CreateBusinessAdminBody,
  CreateBusinessBody,
  UpdateBusinessBody,
  AuditListParams,
  AuditListResult,
  AuditLogEntry,
  CreateUserBody,
  ManagedUser,
  UpdateUserBody,
  CreateOperationRequestBody,
  OperationRequest,
  PendingApprovalCount,
  RequestStatus,
  UpdateOperationRequestBody,
  CreateOperationBody,
  DashboardView,
  ExpenseCategory,
  LedgerReport,
  ListOperationsParams,
  DestinationCandidate,
  ExtraItemsBody,
  LastPricesResult,
  ListMovementsParams,
  ListShipmentsParams,
  ManualShipmentBody,
  MovementListResult,
  MovementsDayGrid,
  MovementsDayGridParams,
  TransferItemBody,
  TransferItemResult,
  MonthlyReport,
  ProductAnalyticsParams,
  ProductAnalyticsReport,
  ProductShipmentsReport,
  MoyskladStatus,
  MsCounterparty,
  Operation,
  OperationListResult,
  PaymentMethod,
  PostShipmentResult,
  PurchaseIssueBody,
  PurchaseIssueResult,
  RefreshShipmentsResult,
  SafeUser,
  SavePricesBody,
  Shipment,
  ShipmentListResult,
  SkladDayDetail,
  SkladDayGrid,
  Sklad,
  SkladSummaryRow,
  Store,
  StoreDayDetail,
  StoreDayGrid,
  StoresSummaryDayGrid,
  TokenPair,
  UpdateOperationBody,
  UpsertCategoryBody,
  UpsertSkladBody,
  UpsertStoreBody,
  WebhookStatus,
} from './types';

// ---------------------------------------------------------------- auth

/** The global store scope as list endpoints take it: ONE store, or the
 * owner's «как видит пользователь X» SET of stores, or nothing (everything). */
export interface StoreScopeParams {
  storeId?: string | null;
  storeIds?: string[] | null;
}

export function scopeParams(scope: StoreScopeParams): { storeId?: string; storeIds?: string[] } {
  if (scope.storeId) return { storeId: scope.storeId };
  if (scope.storeIds && scope.storeIds.length > 0) return { storeIds: scope.storeIds };
  return {};
}

/** Request config for calls that talk to MoySklad or render a PDF. */
const LONG = { timeout: LONG_REQUEST_TIMEOUT_MS };

export const authApi = {
  login: (login: string, password: string) =>
    api.post<TokenPair>('/auth/login', { login, password }).then((r) => r.data),
  logout: (refreshToken: string) =>
    api.post<{ ok: true }>('/auth/logout', { refreshToken }).then((r) => r.data),
  me: () => api.get<SafeUser>('/auth/me').then((r) => r.data),
  changePassword: (oldPassword: string, newPassword: string) =>
    api.patch<{ ok: true }>('/auth/password', { oldPassword, newPassword }).then((r) => r.data),
};

// ---------------------------------------------------------------- users (superadmin)

export const usersApi = {
  list: () => api.get<ManagedUser[]>('/users').then((r) => r.data),
  create: (body: CreateUserBody) => api.post<ManagedUser>('/users', body).then((r) => r.data),
  update: (id: string, body: UpdateUserBody) =>
    api.patch<ManagedUser>(`/users/${id}`, body).then((r) => r.data),
  resetPassword: (id: string, newPassword: string) =>
    api.post<{ ok: true }>(`/users/${id}/reset-password`, { newPassword }).then((r) => r.data),
};

// ---------------------------------------------------------------- businesses (platform admin)

export const businessesApi = {
  list: () => api.get<Business[]>('/businesses').then((r) => r.data),
  create: (body: CreateBusinessBody) => api.post<Business>('/businesses', body).then((r) => r.data),
  update: (id: string, body: UpdateBusinessBody) =>
    api.patch<Business>(`/businesses/${id}`, body).then((r) => r.data),
  createAdmin: (id: string, body: CreateBusinessAdminBody) =>
    api.post<{ id: string }>(`/businesses/${id}/admins`, body).then((r) => r.data),
};

// ---------------------------------------------------------------- audit feed (superadmin)

export const auditApi = {
  list: (params: AuditListParams) =>
    api.get<AuditListResult>('/audit', { params }).then((r) => r.data),
};

// ---------------------------------------------------------------- directories

export const storesApi = {
  list: (includeArchived = false) =>
    api
      .get<Store[]>('/stores', { params: includeArchived ? { includeArchived: true } : {} })
      .then((r) => r.data),
  create: (body: UpsertStoreBody) => api.post<Store>('/stores', body).then((r) => r.data),
  update: (id: string, body: UpsertStoreBody) =>
    api.patch<Store>(`/stores/${id}`, body).then((r) => r.data),
};

export const skladsApi = {
  list: (includeArchived = false) =>
    api
      .get<Sklad[]>('/sklads', { params: includeArchived ? { includeArchived: true } : {} })
      .then((r) => r.data),
  create: (body: UpsertSkladBody) => api.post<Sklad>('/sklads', body).then((r) => r.data),
  update: (id: string, body: UpsertSkladBody) =>
    api.patch<Sklad>(`/sklads/${id}`, body).then((r) => r.data),
};

export const categoriesApi = {
  list: (includeArchived = false) =>
    api
      .get<ExpenseCategory[]>('/expense-categories', {
        params: includeArchived ? { includeArchived: true } : {},
      })
      .then((r) => r.data),
  create: (body: UpsertCategoryBody) =>
    api.post<ExpenseCategory>('/expense-categories', body).then((r) => r.data),
  update: (id: string, body: UpsertCategoryBody) =>
    api.patch<ExpenseCategory>(`/expense-categories/${id}`, body).then((r) => r.data),
};

// ---------------------------------------------------------------- operations

export const operationsApi = {
  create: (body: CreateOperationBody) =>
    api.post<Operation>('/operations', body).then((r) => r.data),
  purchaseIssue: (body: PurchaseIssueBody) =>
    api.post<PurchaseIssueResult>('/operations/purchase-issue', body).then((r) => r.data),
  list: (params: ListOperationsParams) =>
    api.get<OperationListResult>('/operations', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<Operation & { auditLogs: AuditLogEntry[] }>(`/operations/${id}`).then((r) => r.data),
  update: (id: string, body: UpdateOperationBody) =>
    api.patch<Operation>(`/operations/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete<Operation>(`/operations/${id}`).then((r) => r.data),
  restore: (id: string) => api.post<Operation>(`/operations/${id}/restore`).then((r) => r.data),
  setDaySalary: (storeId: string, date: string, amount: number) =>
    api
      .put<{ date: string; salary: number }>('/operations/store-day-salary', {
        storeId,
        date,
        amount,
      })
      .then((r) => r.data),
  setDayPayment: (storeId: string, date: string, method: PaymentMethod, amount: number) =>
    api
      .put<{ date: string; method: PaymentMethod; amount: number }>(
        '/operations/store-day-payment',
        {
          storeId,
          date,
          method,
          amount,
        },
      )
      .then((r) => r.data),
};

// ------------------------------------------------- operation requests (approval)

export const operationRequestsApi = {
  // Seller side.
  listMine: (status?: RequestStatus) =>
    api
      .get<OperationRequest[]>('/operation-requests', { params: status ? { status } : {} })
      .then((r) => r.data),
  // `sourceKey` rides in a header, not the body: it is a transport concern, it
  // matches the riba-market integration route, and the axios 401-refresh replay
  // re-sends the SAME config — so the replay is idempotent for free.
  create: (body: CreateOperationRequestBody, sourceKey?: string) =>
    api
      .post<OperationRequest>('/operation-requests', body, {
        headers: sourceKey ? { 'X-Source-Key': sourceKey } : undefined,
      })
      .then((r) => r.data),
  update: (id: string, body: UpdateOperationRequestBody) =>
    api.patch<OperationRequest>(`/operation-requests/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete<{ ok: true }>(`/operation-requests/${id}`).then((r) => r.data),
  // Admin side.
  listPending: (scope: StoreScopeParams) =>
    api
      .get<OperationRequest[]>('/operation-requests/pending', { params: scopeParams(scope) })
      .then((r) => r.data),
  pendingCounts: () =>
    api.get<PendingApprovalCount[]>('/operation-requests/pending/counts').then((r) => r.data),
  approve: (id: string, confirmDuplicate = false) =>
    api
      .post<OperationRequest>(
        `/operation-requests/${id}/approve`,
        confirmDuplicate ? { confirmDuplicate: true } : {},
      )
      .then((r) => r.data),
  approveBatch: (ids: string[], confirmDuplicateIds?: string[]) =>
    api
      .post<ApproveBatchResult>('/operation-requests/approve-batch', {
        ids,
        ...(confirmDuplicateIds?.length ? { confirmDuplicateIds } : {}),
      })
      .then((r) => r.data),
  reject: (id: string, note?: string) =>
    api
      .post<OperationRequest>(`/operation-requests/${id}/reject`, note ? { note } : {})
      .then((r) => r.data),
};

// ---------------------------------------------------------------- reports

export const reportsApi = {
  monthly: (month: string, scope: StoreScopeParams) =>
    api
      .get<MonthlyReport>('/reports/monthly', { params: { month, ...scopeParams(scope) } })
      .then((r) => r.data),
  storeLedger: (id: string, month: string) =>
    api
      .get<LedgerReport>(`/reports/stores/${id}/ledger`, { params: { month } })
      .then((r) => r.data),
  skladLedger: (id: string, month: string) =>
    api
      .get<LedgerReport>(`/reports/sklads/${id}/ledger`, { params: { month } })
      .then((r) => r.data),
  skladsSummary: () => api.get<SkladSummaryRow[]>('/reports/sklads').then((r) => r.data),
  storeDayGrid: (id: string, month: string) =>
    api
      .get<StoreDayGrid>(`/reports/stores/${id}/day-grid`, { params: { month } })
      .then((r) => r.data),
  skladDayGrid: (id: string, month: string) =>
    api
      .get<SkladDayGrid>(`/reports/sklads/${id}/day-grid`, { params: { month } })
      .then((r) => r.data),
  storesSummaryDayGrid: (month: string) =>
    api
      .get<StoresSummaryDayGrid>('/reports/summary/day-grid', { params: { month } })
      .then((r) => r.data),
  storeDay: (id: string, date: string) =>
    api.get<StoreDayDetail>(`/reports/stores/${id}/day`, { params: { date } }).then((r) => r.data),
  skladDay: (id: string, date: string) =>
    api.get<SkladDayDetail>(`/reports/sklads/${id}/day`, { params: { date } }).then((r) => r.data),
  /** «Аналитика по товарам» — admin-only (buy prices). */
  products: (params: ProductAnalyticsParams, scope: StoreScopeParams) =>
    api
      .get<ProductAnalyticsReport>('/reports/products', {
        params: { ...params, ...scopeParams(scope) },
      })
      .then((r) => r.data),
  productShipments: (key: string, params: ProductAnalyticsParams, scope: StoreScopeParams) =>
    api
      .get<ProductShipmentsReport>(`/reports/products/${encodeURIComponent(key)}/shipments`, {
        params: { ...params, ...scopeParams(scope) },
      })
      .then((r) => r.data),
};

// ---------------------------------------------------------------- moysklad / накладные

export const shipmentsApi = {
  list: (params: ListShipmentsParams) =>
    api.get<ShipmentListResult>('/shipments', { params }).then((r) => r.data),
  get: (id: string) => api.get<Shipment>(`/shipments/${id}`).then((r) => r.data),
  /** Download a накладная PDF. `view='store'` → issue-price-only copy;
   * `view='sklad'` → full owner copy (admin only). Returns the blob + the
   * server-suggested filename (Content-Disposition). */
  downloadPdf: (id: string, view: 'store' | 'sklad', skladId?: string) =>
    api
      .get(`/shipments/${id}/pdf`, {
        params: { view, skladId },
        responseType: 'blob',
        timeout: LONG_REQUEST_TIMEOUT_MS,
      })
      .then((r) => ({
        blob: r.data as Blob,
        filename: filenameFromContentDisposition(
          r.headers['content-disposition'] as string | undefined,
        ),
      })),
  productNames: () => api.get<string[]>('/shipments/product-names').then((r) => r.data),
  lastPrices: (storeId: string) =>
    api
      .get<LastPricesResult>('/shipments/last-prices', { params: { storeId } })
      .then((r) => r.data),
  createManual: (body: ManualShipmentBody) =>
    api.post<Shipment>('/shipments', body).then((r) => r.data),
  updateManual: (id: string, body: ManualShipmentBody) =>
    api.patch<Shipment>(`/shipments/${id}`, body).then((r) => r.data),
  remove: (id: string) => api.delete<{ ok: true }>(`/shipments/${id}`).then((r) => r.data),
  savePrices: (id: string, body: SavePricesBody) =>
    api.put<Shipment>(`/shipments/${id}/prices`, body).then((r) => r.data),
  saveExtraItems: (id: string, body: ExtraItemsBody) =>
    api.put<Shipment>(`/shipments/${id}/extra-items`, body).then((r) => r.data),
  post: (id: string) =>
    api.post<PostShipmentResult>(`/shipments/${id}/post`, undefined, LONG).then((r) => r.data),
  unpost: (id: string) =>
    api.post<Shipment>(`/shipments/${id}/unpost`, undefined, LONG).then((r) => r.data),
  destinationCandidates: (storeId: string, date: string) =>
    api
      .get<DestinationCandidate[]>('/shipments/destination-candidates', {
        params: { storeId, date },
      })
      .then((r) => r.data),
  transferItem: (id: string, itemId: string, body: TransferItemBody) =>
    api
      .post<TransferItemResult>(`/shipments/${id}/items/${itemId}/transfer`, body, LONG)
      .then((r) => r.data),
  returnItem: (id: string, itemId: string, body: { quantityMilli: number }) =>
    api.post<Shipment>(`/shipments/${id}/items/${itemId}/return`, body, LONG).then((r) => r.data),
  undoMovement: (id: string, movementId: string) =>
    api
      .post<Shipment>(`/shipments/${id}/movements/${movementId}/undo`, undefined, LONG)
      .then((r) => r.data),
  /** Re-run the MoySklad «Возврат покупателя» draft mirror («Повторить»). */
  syncMsReturn: (id: string) =>
    api.post<Shipment>(`/shipments/${id}/ms-return/sync`, undefined, LONG).then((r) => r.data),
  refresh: (days?: number) =>
    api
      .post<RefreshShipmentsResult>('/shipments/refresh', days ? { days } : {}, LONG)
      .then((r) => r.data),
};

// ---------------------------------------------------------------- перемещения

export const movementsApi = {
  list: (params: ListMovementsParams) =>
    api.get<MovementListResult>('/movements', { params }).then((r) => r.data),
  dayGrid: (params: MovementsDayGridParams) =>
    api.get<MovementsDayGrid>('/movements/day-grid', { params }).then((r) => r.data),
};

export const moyskladApi = {
  status: () => api.get<MoyskladStatus>('/moysklad/settings').then((r) => r.data),
  setSklad: (skladId: string | null) =>
    api.put<MoyskladStatus>('/moysklad/settings', { skladId }).then((r) => r.data),
  counterparties: () => api.get<MsCounterparty[]>('/moysklad/counterparties').then((r) => r.data),
  webhooks: () => api.get<WebhookStatus>('/moysklad/webhooks').then((r) => r.data),
  registerWebhooks: () =>
    api.post<WebhookStatus>('/moysklad/webhooks/register', undefined, LONG).then((r) => r.data),
};

// ---------------------------------------------------------------- dashboard

export const dashboardApi = {
  view: (scope: StoreScopeParams) =>
    api.get<DashboardView>('/dashboard', { params: scopeParams(scope) }).then((r) => r.data),
};
